/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, ExternalLink, ChevronDown, ChevronUp, Users } from "lucide-react";
import { api } from "../../lib/api";

const STAGES = [
  { key: "new_lead",                     label: "New Lead",                  dot: "#8a7e6d" },
  { key: "efficiency_review_requested",  label: "Review Requested",          dot: "#d4a04a" },
  { key: "review_scheduled",             label: "Review Scheduled",          dot: "#d4a04a" },
  { key: "review_completed",             label: "Review Completed",          dot: "#b8963e" },
  { key: "assessment_offered",           label: "Assessment Offered",        dot: "#b8963e" },
  { key: "assessment_paid",              label: "Assessment Paid",           dot: "#6db66d" },
  { key: "workflow_sprint_proposed",     label: "Sprint Proposed",           dot: "#b8963e" },
  { key: "workflow_sprint_sold",         label: "Sprint Sold",               dot: "#6db66d" },
  { key: "monthly_support_offered",      label: "Monthly Offered",           dot: "#d4a04a" },
  { key: "monthly_support_active",       label: "Monthly Active",            dot: "#6db66d" },
  { key: "closed_lost",                  label: "Closed Lost",               dot: "#5e5448" },
];

export default function AdminOperations() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/operations/leads");
      setData(data);
      setError("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not load leads.");
    }
  };

  useEffect(() => { load(); }, []);

  const byStage = useMemo(() => {
    const map = Object.fromEntries(STAGES.map((s) => [s.key, []]));
    (data?.items || []).forEach((lead) => {
      const key = lead.stage || "new_lead";
      (map[key] ||= []).push(lead);
    });
    return map;
  }, [data]);

  const updateLead = async (id, patch) => {
    setSaving(id);
    try {
      await api.patch(`/admin/operations/leads/${id}`, patch);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Update failed.");
    } finally {
      setSaving(null);
    }
  };

  const deleteLead = async (id) => {
    if (!window.confirm("Delete this lead? This cannot be undone.")) return;
    setSaving(id);
    try {
      await api.delete(`/admin/operations/leads/${id}`);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Delete failed.");
    } finally {
      setSaving(null);
    }
  };

  if (!data) return (
    <div className="min-h-screen bg-[#080807] text-[#f4efe6] px-6 py-12">
      <p className="text-[#8a7e6d]">Loading operations pipeline…</p>
    </div>
  );

  return (
    <div data-testid="admin-operations-page" className="min-h-screen bg-[#080807] text-[#f4efe6]">
      {/* Header */}
      <section className="border-b border-[#2a2620]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-8 py-10">
          <p className="overline">AI Operations · Pipeline</p>
          <div className="mt-2 flex flex-wrap items-baseline justify-between gap-4">
            <h1 className="font-serif-display text-[36px] sm:text-[44px] text-[#f4efe6]" style={{ fontWeight: 600 }}>
              Efficiency Review pipeline
            </h1>
            <div className="flex items-center gap-6">
              <Stat label="Leads" value={data.total} icon={<Users className="w-4 h-4" />} />
              <Stat label="Revenue" value={`$${(data.total_revenue_usd || 0).toFixed(0)}`} />
              <Stat label="MRR" value={`$${(data.total_mrr_usd || 0).toFixed(0)}`} />
            </div>
          </div>
          {error && <p className="mt-4 text-sm text-[#c46a5a]" data-testid="ops-error">{error}</p>}
          <div className="mt-4 flex flex-wrap gap-3 text-xs">
            <Link to="/admin" className="btn-ghost">← Visibility admin</Link>
          </div>
        </div>
      </section>

      {/* Stage column summary bar */}
      <section className="border-b border-[#2a2620] overflow-x-auto">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-8 py-4 flex gap-6 min-w-max">
          {STAGES.map((s) => (
            <div key={s.key} className="flex items-center gap-2 text-xs">
              <span className="inline-block w-2 h-2 rounded-full" style={{ background: s.dot }} />
              <span className="text-[#c9bca4]">{s.label}</span>
              <span className="text-[#8a7e6d]">({(data.counts?.[s.key] ?? 0)})</span>
            </div>
          ))}
        </div>
      </section>

      {/* Lead list — grouped by stage */}
      <section>
        <div className="max-w-[1400px] mx-auto px-6 sm:px-8 py-8 space-y-10">
          {STAGES.map((stage) => {
            const leads = byStage[stage.key] || [];
            if (leads.length === 0) return null;
            return (
              <div key={stage.key} data-testid={`stage-group-${stage.key}`}>
                <div className="flex items-center gap-3">
                  <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: stage.dot }} />
                  <h2 className="font-mono-eav text-[11px] tracking-[0.22em] uppercase text-[#c9bca4]">
                    {stage.label} <span className="text-[#8a7e6d]">· {leads.length}</span>
                  </h2>
                </div>
                <div className="mt-4 grid gap-3">
                  {leads.map((lead) => (
                    <LeadRow key={lead.id} lead={lead}
                             expanded={expandedId === lead.id}
                             onExpand={() => setExpandedId(expandedId === lead.id ? null : lead.id)}
                             saving={saving === lead.id}
                             onUpdate={(patch) => updateLead(lead.id, patch)}
                             onDelete={() => deleteLead(lead.id)} />
                  ))}
                </div>
              </div>
            );
          })}
          {data.total === 0 && (
            <div className="border border-dashed border-[#2a2620] p-10 text-center">
              <p className="text-[#8a7e6d]">No AI Operations leads yet. When someone books an Efficiency Review or purchases an Assessment, they'll appear here.</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value, icon }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="font-serif-display text-2xl text-[#f4efe6]" style={{ fontWeight: 700 }}>{value}</span>
      <span className="font-mono-eav text-[9px] tracking-[0.22em] uppercase text-[#8a7e6d] flex items-center gap-1">
        {icon}{label}
      </span>
    </div>
  );
}

function LeadRow({ lead, expanded, onExpand, saving, onUpdate, onDelete }) {
  const [notes, setNotes] = useState(lead.notes || "");
  const [nextAction, setNextAction] = useState(lead.next_action_at || "");
  const [revenue, setRevenue] = useState(lead.revenue_usd || 0);
  const [mrr, setMrr] = useState(lead.mrr_usd || 0);

  return (
    <div className="border border-[#2a2620] bg-[#0e0c0a]" data-testid={`ops-lead-${lead.id}`}>
      <div className="flex items-center gap-4 p-4 cursor-pointer" onClick={onExpand}>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-3 flex-wrap">
            <p className="text-[#f4efe6] font-medium">{lead.business_name || "—"}</p>
            <p className="text-sm text-[#c9bca4]">{lead.first_name} {lead.last_name}</p>
            <a href={`mailto:${lead.work_email}`} onClick={(e) => e.stopPropagation()}
               className="text-sm text-[#b8963e] hover:underline">{lead.work_email}</a>
            {lead.industry && <span className="text-xs text-[#8a7e6d]">· {lead.industry}</span>}
            {lead.employees && <span className="text-xs text-[#8a7e6d]">· {lead.employees}</span>}
          </div>
          <p className="mt-1 text-xs text-[#8a7e6d]">
            {new Date(lead.created_at).toLocaleDateString()} · {lead.source}
            {lead.website && <> · <a href={lead.website} target="_blank" rel="noreferrer"
                                     onClick={(e) => e.stopPropagation()}
                                     className="text-[#b8963e] hover:underline inline-flex items-center gap-1">
              {lead.website.replace(/^https?:\/\//, "").slice(0, 40)} <ExternalLink className="w-3 h-3" />
            </a></>}
          </p>
        </div>
        <select
          value={lead.stage}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => onUpdate({ stage: e.target.value })}
          data-testid={`stage-select-${lead.id}`}
          disabled={saving}
          className="bg-[#0a0908] border border-[#2a2620] text-[#f4efe6] text-xs px-3 py-2"
        >
          {STAGES.map((s) => (
            <option key={s.key} value={s.key}>{s.label}</option>
          ))}
        </select>
        <button onClick={(e) => { e.stopPropagation(); onDelete(); }}
                data-testid={`delete-lead-${lead.id}`}
                disabled={saving}
                title="Delete lead"
                className="p-2 text-[#8a7e6d] hover:text-[#c46a5a] transition-colors">
          <Trash2 className="w-4 h-4" />
        </button>
        {expanded ? <ChevronUp className="w-4 h-4 text-[#8a7e6d]" /> : <ChevronDown className="w-4 h-4 text-[#8a7e6d]" />}
      </div>

      {expanded && (
        <div className="border-t border-[#2a2620] p-5 grid md:grid-cols-2 gap-6 text-sm">
          <div className="space-y-3">
            <Detail label="Phone" value={lead.phone} />
            <Detail label="Primary challenge" value={lead.primary_challenge} />
            <Detail label="Time-consuming task" value={lead.time_consuming_task} />
            <Detail label="Lead channels" value={lead.lead_channels} />
            <Detail label="Software used" value={lead.software_used} />
            <Detail label="Preferred time" value={lead.preferred_time} />
          </div>
          <div className="space-y-3">
            <label className="block">
              <span className="block font-mono-eav text-[10px] tracking-[0.22em] uppercase text-[#8a7e6d] mb-1">Notes</span>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                        onBlur={() => notes !== (lead.notes || "") && onUpdate({ notes })}
                        rows={3}
                        className="w-full bg-[#0a0908] border border-[#2a2620] focus:border-[#b8963e] outline-none text-[#f4efe6] px-3 py-2 text-sm" />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block font-mono-eav text-[10px] tracking-[0.22em] uppercase text-[#8a7e6d] mb-1">Next action</span>
                <input type="text" value={nextAction} onChange={(e) => setNextAction(e.target.value)}
                       onBlur={() => nextAction !== (lead.next_action_at || "") && onUpdate({ next_action_at: nextAction })}
                       placeholder="e.g. Follow up Tue"
                       className="w-full bg-[#0a0908] border border-[#2a2620] focus:border-[#b8963e] outline-none text-[#f4efe6] px-3 py-2 text-sm" />
              </label>
              <label className="block">
                <span className="block font-mono-eav text-[10px] tracking-[0.22em] uppercase text-[#8a7e6d] mb-1">Revenue ($)</span>
                <input type="number" step="1" value={revenue} onChange={(e) => setRevenue(Number(e.target.value) || 0)}
                       onBlur={() => Number(revenue) !== Number(lead.revenue_usd || 0) && onUpdate({ revenue_usd: Number(revenue) })}
                       className="w-full bg-[#0a0908] border border-[#2a2620] focus:border-[#b8963e] outline-none text-[#f4efe6] px-3 py-2 text-sm" />
              </label>
            </div>
            <label className="block">
              <span className="block font-mono-eav text-[10px] tracking-[0.22em] uppercase text-[#8a7e6d] mb-1">Monthly recurring revenue ($/mo)</span>
              <input type="number" step="1" value={mrr} onChange={(e) => setMrr(Number(e.target.value) || 0)}
                     onBlur={() => Number(mrr) !== Number(lead.mrr_usd || 0) && onUpdate({ mrr_usd: Number(mrr) })}
                     className="w-full bg-[#0a0908] border border-[#2a2620] focus:border-[#b8963e] outline-none text-[#f4efe6] px-3 py-2 text-sm" />
            </label>
          </div>
        </div>
      )}
    </div>
  );
}

function Detail({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <p className="font-mono-eav text-[10px] tracking-[0.22em] uppercase text-[#8a7e6d]">{label}</p>
      <p className="mt-1 text-[#c9bca4] whitespace-pre-wrap">{value}</p>
    </div>
  );
}
